export interface LastUserLogin {
  email?: string;
  name?: string;
  picture?: string;
  connection?: string;
}

/**
 * Validates and parses LastUserLogin data from localStorage
 * @param data Raw data from localStorage
 * @returns Validated LastUserLogin object or null if invalid
 */
export const validateLastUserLogin = (data: string | null): LastUserLogin | null => {
  if (!data) {
    return null;
  }

  try {
    const parsed = JSON.parse(data) as unknown;

    // Check if it's an object
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    // Validate that required fields are strings if present
    const { email, name, picture, connection } = parsed as Record<string, unknown>;

    if (email && typeof email !== 'string') {
      return null;
    }

    if (name && typeof name !== 'string') {
      return null;
    }

    if (picture && typeof picture !== 'string') {
      return null;
    }

    if (connection && typeof connection !== 'string') {
      return null;
    }

    // Return validated object with only known properties
    return {
      email: email as string | undefined,
      name: name as string | undefined,
      picture: picture as string | undefined,
      connection: connection as string | undefined,
    };
  } catch (error) {
    console.error('Failed to parse lastUserLogin:', error);
    return null;
  }
};

/**
 * Safely retrieves and validates LastUserLogin from localStorage
 * @returns Validated LastUserLogin object or empty object if invalid/missing
 */
export const getLastUserLogin = (): LastUserLogin => {
  const storedData = localStorage.getItem('lastUserLogin');
  const validated = validateLastUserLogin(storedData);

  if (!validated) {
    // Clear corrupted data
    localStorage.removeItem('lastUserLogin');
    return {};
  }

  return validated;
};

/**
 * Safely stores LastUserLogin to localStorage
 * @param lastUserLogin The user login data to store
 */
export const setLastUserLogin = (lastUserLogin: LastUserLogin): void => {
  try {
    localStorage.setItem('lastUserLogin', JSON.stringify(lastUserLogin));
  } catch (error) {
    console.error('Failed to store lastUserLogin:', error);
  }
};
